import React, { useState } from "react";
import Sidebar from "../../components/Sidebar";
import AdminDashboardCard from "../../components/card/AdminDashboardCard";
import People from "../../images/svgs/People";
import Calender from "../../images/svgs/Calender";
import Book from "../../images/svgs/Book";
import Eye from "../../images/svgs/Eye";
import { MessageCircle } from "lucide-react";
import { useSelector } from "react-redux";
import { getMentorAppointments } from "../../services/dashboardService";
import { useQuery } from "@tanstack/react-query";
import Modal from "../../components/modal/Modal";
import { Link } from "react-router-dom";
import { workYears } from "../../constants/constants";
import moment from "moment-timezone";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";

const statusColors = {
  UPCOMING: "bg-pending text-white",
  COMPLETED: "bg-accept text-white",
  CANCELED: "bg-reject text-white",
};

const MentorHome = () => {
  const LabelValue = ({ label, value }) => {
    return (
      <p className="flex flex-col items-left text-[#212529]">
        <span className="font-semibold">{label}</span>
        <span className="font-bold">{value}</span>
      </p>
    );
  };
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null); 
  const userId = useSelector((state) => state.auth.id);
  const mentorTimezone = useSelector((state) => state.mentor.profile.timezone);
  const effectiveTimezone = mentorTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data: appointmentsData, isLoading: appointmentsLoading } = useQuery({
    queryKey: ["mentor_appointments", userId],
    queryFn: () => getMentorAppointments(userId),
    enabled: !!userId,
  });

  const openModal = (appointment) => {
    setSelectedAppointment(appointment);
    setIsOpen(true);
  };

  const timeConversion = (input) => {
    const [startUtc, endUtc] = input.split(" - ");
    const start = moment.utc(startUtc).tz(effectiveTimezone);
    const end = moment.utc(endUtc).tz(effectiveTimezone);

    const output = {
      date: start.format("DD-MM-YYYY"),
      slot: `${start.format("HH:mm")}-${end.format("HH:mm")}`,
    };
    return output;
  };
  const sortedAppointments =
    appointmentsData &&
    [...appointmentsData]
      .filter((appointment) => {
        // Use the date field consistently for filtering
        const appointmentDate = moment.utc(appointment.date).tz(effectiveTimezone);
        const now = moment().tz(effectiveTimezone);
        return appointmentDate?.isSameOrAfter(now);  
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date)); 

  const menteesCount = appointmentsData ? new Set(appointmentsData.map(appt => appt.menteesId)).size : 0;

  const upcomingAppointmentsCount = appointmentsData 
    ? appointmentsData.filter(appt => {
        const appointmentDate = moment.utc(appt.date).tz(effectiveTimezone);
        const now = moment().tz(effectiveTimezone);
        return appointmentDate?.isSameOrAfter(now);
      }).length 
    : 0;


  return (
    <Sidebar>
      <div className="p-6 bg-white min-h-screen">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <AdminDashboardCard
          icon={<People />}
          bgColor={"#edf3ff"}
          innerBg={"#dce8ff"}
          quantity={menteesCount}
          label={"Mentees"}
        />
        <AdminDashboardCard
          icon={<Calender />}
          bgColor={"#fefae9"}
          innerBg={"#fdf6d5"}
          quantity={upcomingAppointmentsCount ? upcomingAppointmentsCount : 0}          
          label={"Appointments"}
        />
        <AdminDashboardCard
          icon={<Book />}
          bgColor={"#ffeef2"}
          innerBg={"#ffdee7"}
          quantity={"$14"}
          label={"Total Earned"}
        />
      </div>

      {/* Appointment Details Modal */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width="w-[700px]"
        label={"Appointment Details"}
      >
        {selectedAppointment && (
          <div>
            <div className="mx-auto mb-5">
              {/* Personal Details */}
              <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2  borber-b border-custom-blue">
                <h3 className="text-lg font-semibold">MENTEE DETAILS</h3>
                <div className="border-b pt-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 text-gray-600">
                  <LabelValue
                    label={"NAME"}
                    value={`${selectedAppointment?.menteeDetails?.firstName} ${selectedAppointment?.menteeDetails?.lastName}`}
                  />
                  <LabelValue
                    label={"Mentee Current Job/Occupation"}
                    value={`${selectedAppointment?.menteeDetails?.currentJob}`}
                  />
                  <LabelValue
                    label={"Mentee Years of Work Experience"}
                    value={`${
                      workYears.find(
                        (skill) =>
                          skill.value ===
                          selectedAppointment?.menteeDetails?.yearsOfExperience
                      )?.label
                    }`}
                  />
                  <LabelValue
                    label={"Mentee Selected Target Occupation"}
                    value={`${selectedAppointment?.menteeDetails?.targetJob}`}
                  />
                  <LabelValue
                    label={"Mentee Career Goals"}
                    value={`${selectedAppointment?.menteeApplication?.careerGoals}`}
                  />
                  <LabelValue
                    label={"Mentorship Goals"}
                    value={`${selectedAppointment?.menteeApplication?.mentorshipGoals}`}
                  />
                  <LabelValue
                    label={"Current Challenges"}
                    value={`${selectedAppointment?.menteeApplication?.currentChallenges}`}
                  />
                </div>
              </div>
              <div className="bg-white shadow-md rounded-lg p-6 mt-6 border-b-2  borber-b border-custom-blue">
                <h3 className="text-lg font-semibold">APPOINTMENT DETAILS</h3>
                <div className="border-b pt-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 text-gray-600">
                  <LabelValue
                    label={"DATE"}
                    value={
                      timeConversion(selectedAppointment.selectedTime).date
                    }
                  />
                  <LabelValue
                    label={"TIME SLOT"}
                    value={
                      timeConversion(selectedAppointment.selectedTime).slot
                    }
                  />
                  <LabelValue
                    label={"Duration"}
                    value={`${selectedAppointment.meetingForTime} MINUTES`}
                  />
                  <LabelValue
                    label={"STATUS"}
                    value={
                      selectedAppointment.status === 0
                        ? "UPCOMING"
                        : selectedAppointment.status === 1
                        ? "COMPLETED"
                        : "CANCELED"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Appointments Table */}
      <div className="w-full overflow-x-auto">
        <h3 className="text-lg font-semibold mb-4">Upcoming Appointments</h3>
        {appointmentsLoading ? (
          <SecondaryLoader message="Loading appointments..." />
        ) : (
          <table className="w-full border-collapse">
            <thead className="border-t border-b">
              <tr className="uppercase text-sm">
                <th className="py-3 px-6 text-left  text-nowrap">MENTEE NAME</th>
                <th className="py-3 px-6 text-left  text-nowrap">
                  APPOINTMENT DATE
                </th>
                <th className="py-3 px-6 text-left  text-nowrap">
                  APPOINTMENT SLOT
                </th>
                <th className="py-3 px-6 text-left text-nowrap">TAGS</th>
                <th className="py-3 px-6 text-left pl-10 text-nowrap">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {sortedAppointments && sortedAppointments.length > 0 ? (
                sortedAppointments.map((appointment, index) => (
              <tr key={index} className="border-b">
                <td className="py-4 px-6">
                  <div className="font-medium">
                    {`${appointment?.menteeDetails?.firstName} ${appointment?.menteeDetails?.lastName}`}
                  </div>
                </td>
                <td className="py-4 px-6">
                  {timeConversion(appointment.selectedTime).date}
                </td>
                <td className="py-4 px-6">
                  {" "}
                  {timeConversion(appointment.selectedTime).slot}
                </td>
                <td className="text-center">
                  <span
                    className={`px-3 py-1 rounded text-sm font-semibold ${
                      appointment.status === 0
                        ? statusColors["UPCOMING"]
                        : appointment.status === 1
                        ? statusColors["COMPLETED"]
                        : statusColors["CANCELED"]
                    }`}
                  >
                    {appointment.status === 0
                      ? "UPCOMING"
                      : appointment.status === 1
                      ? "COMPLETED"
                      : "CANCELED"}
                  </span>
                </td>
                <td className="py-4 text-nowrap">
                  <div className="ml-10 flex gap-5">
                    <button
                      onClick={() => openModal(appointment)}
                      className="text-[#1db9aa] hover:underline flex items-center gap-2 bg-[#e1f6f6] p-1 px-3 rounded"
                    >
                      <Eye className="text-[#1db9aa] w-6" /> View
                    </button>
                    <Link
                      to={`/dashboard/chat/${appointment.menteesId}`}
                      className="text-blue-800 hover:underline flex items-center
                      justify-center gap-2 bg-blue-100 p-1 px-3 rounded
                      relative"
                    >
                      <MessageCircle className="w-5" />
                      Chat with Mentee
                    </Link>
                    <Link to={appointment?.meetingLink}>
                      {" "}
                      <button className="text-[#1D1E9D] hover:underline flex items-center justify-center gap-2 bg-[#1D1E9D12] p-1 px-3 rounded relative">
                        <div
                          className="bg-green-600 rounded-full"
                          style={{
                            width: "5.43px",
                            height: "5.43px",
                          }}
                        ></div>
                        Join Meeting
                      </button>
                    </Link>
                  </div>
                </td>
              </tr>
              ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 px-6 text-center text-gray-500">
                    No upcoming appointments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </Sidebar>
  );
};

export default MentorHome;
